import { useQuery } from '@tanstack/react-query';

import { QueryKey } from '@/config/query-client';
import { isIdNotRecognizedError } from '@/helpers/is-id-not-recognized-error';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { useEffect } from 'react';
import { fetchCompanyProducts } from '@/api/v4/organization.api';
import { fetchSpotlightCompanyProducts } from '@/api/v4/spotlight.api';
import { companyIdState } from '../company.state';
import { useAtomValue } from 'jotai';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '../company-fetch-context';

const companyProductsFetchers: Record<
  CompanyFetchMode,
  (companyId: string) => ReturnType<typeof fetchCompanyProducts>
> = {
  general: fetchCompanyProducts,
  spotlight: fetchSpotlightCompanyProducts,
};

export const useCompanyProducts = () => {
  const { hasAtLeastOnePermission } = useUserPermissions();
  const companyId = useAtomValue(companyIdState);
  const navigateTo = useNavigateTo();
  const fetchMode = useCompanyFetchMode();

  const getCompanyProducts = () => {
    if (!companyId) return;
    return companyProductsFetchers[fetchMode](companyId);
  };

  const query = useQuery({
    queryKey: [QueryKey.CompanyProducts, fetchMode, companyId],
    queryFn: getCompanyProducts,
    retry: false,
    enabled:
      !!companyId &&
      hasAtLeastOnePermission([PERMISSION.VIEW_ORGANIZATION_HOME_SCREEN]),
  });

  useEffect(() => {
    if (fetchMode === 'spotlight') return;

    if (query.error && isIdNotRecognizedError(query.error)) {
      navigateTo.notFound();
    }
  }, [query.error, fetchMode, navigateTo]);

  return query;
};
